import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { AppContextProvider, Env } from '../../contexts/AppContext';
import { useScrollToTop } from '../../hooks';
import useCurrentUser from '../../hooks/useCurrentUser';
import { User } from '../../utils/backend-api/currentUser';
import { CustomToaster } from '../BaseUI';
import CommunityInsightsPage from '../CommunityInsightsPage';
import EditForumPostPage from '../Forum/EditForumPostPage';
import ForumIndexPage from '../Forum/ForumIndexPage';
import ForumLayout from '../Forum/ForumLayout';
import ForumPostPage from '../Forum/ForumPostPage';
import Partners from '../Partners';
import SettingsPage from '../Settings/SettingsPage';
import Team from '../Settings/SettingsPage/Team';
import Subscriber from '../Subscriber';
import SubscriberDirectory from '../SubscriberDirectory';
import MetaTagLayout from './MetaTagLayout';

const queryClient = new QueryClient();

function AppRoutes() {
  useScrollToTop();
  const [params] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();

  // Set the rc param on every location change
  useEffect(() => {
    if (currentUser?.affiliate_code && !params.get('rc')) {
      params.set('rc', currentUser.affiliate_code);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [params, currentUser?.affiliate_code, navigate]);

  return (
    <Routes>
      <Route element={<MetaTagLayout />}>
        <Route element={<ForumLayout />}>
          <Route path="/forum" element={<ForumIndexPage />} />
          <Route path="/forum/recent" element={<ForumIndexPage order="most_recent" />} />
          <Route path="/forum/comments" element={<ForumIndexPage order="most_comments" />} />
          <Route path="/forum/posts/:id" element={<ForumPostPage />} />
          <Route path="/forum/posts/:id/edit" element={<EditForumPostPage />} />
          <Route path="/forum/posts/new" element={<EditForumPostPage />} />
        </Route>
        <Route path="/partners" element={<Partners />} />
        <Route path="/insights" element={<CommunityInsightsPage />} />
        <Route path="/directory" element={<SubscriberDirectory />} />
        <Route path="/u/:username" element={<Subscriber />} />
        <Route path="/u/:username/:article_page_id" element={<Subscriber />} />
        <Route path="/settings" element={<SettingsPage />}>
          <Route path="team-settings" element={<Team />} />
        </Route>
      </Route>
    </Routes>
  );
}

const App = ({
  env,
  currentUser,
  isWebview,
}: {
  env: Env;
  currentUser: User;
  isWebview: boolean;
}) => {
  return (
    <AppContextProvider env={env} initialCurrentUser={currentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <BrowserRouter>
            <AppRoutes />
            <CustomToaster />
          </BrowserRouter>
        </div>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default App;
